import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ChevronRight, ChevronLeft, IndianRupee, Loader2 } from "lucide-react";
import { TIME_SLOTS, WHATSAPP_NUMBER, formatTime12h } from "@/lib/lime-data";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HTTP_CONFLICT = 409;

const stepBadgeClass = (active, done) => {
  if (done) return "bg-[#E4FF00] text-black border-[#E4FF00]";
  if (active) return "border-[#E4FF00] text-[#E4FF00]";
  return "border-[#262626] text-[#666]";
};

const timeSlotClass = (selected, isBooked) => {
  if (selected) return "bg-[#E4FF00] text-black border-[#E4FF00]";
  if (isBooked) return "border-[#262626] text-[#555] line-through cursor-not-allowed";
  return "border-[#262626] text-[#f4f4f0] hover:border-[#E4FF00]";
};

const StepBadge = ({ n, label, active, done }) => (
  <div className="flex items-center gap-3">
    <div
      className={`h-8 w-8 flex items-center justify-center text-xs font-mono border ${stepBadgeClass(active, done)}`}
    >
      {done ? <CheckCircle2 className="h-4 w-4" /> : n}
    </div>
    <span className={`text-xs font-mono uppercase tracking-widest ${active || done ? "text-[#f4f4f0]" : "text-[#666]"}`}>{label}</span>
  </div>
);

export default function BookingDialog({ open, onOpenChange, initialPackage, initialOption, packages }) {
  const [step, setStep] = useState(1);
  const [packageId, setPackageId] = useState(initialPackage?.id || "");
  const [optionDuration, setOptionDuration] = useState(initialOption?.duration || "");
  const [hours, setHours] = useState(1);
  const [date, setDate] = useState();
  const [startTime, setStartTime] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [details, setDetails] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [waConfirmUrl, setWaConfirmUrl] = useState("");
  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // {code, discount, final_amount}
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);

  const selectedPackage = useMemo(
    () => packages.find((p) => p.id === packageId),
    [packages, packageId]
  );
  const selectedOption = useMemo(
    () => selectedPackage?.options.find((o) => o.duration === optionDuration),
    [selectedPackage, optionDuration]
  );

  const isPerHour = optionDuration?.toLowerCase().startsWith("per hour");
  const effectiveHours = isPerHour ? Math.max(1, Number(hours) || 1) : null;

  // Display options for step 2: for packages with "Per Hour", expand into a full
  // 1..N hour price ladder (same math as /pricing). Clicking any row either picks
  // the fixed-tier option directly or resolves to Per Hour × N hours behind the scenes.
  const displayOptions = useMemo(() => {
    if (!selectedPackage) return [];
    const opts = selectedPackage.options;
    const perHour = opts.find((o) => /per hour/i.test(o.duration));
    if (!perHour) return opts.map((o) => ({ duration: o.duration, price: o.price, mode: "fixed", underlying: o }));

    const perHourRate = perHour.price;
    const fixed = opts
      .filter((o) => o !== perHour)
      .map((o) => {
        const m = /(\d+)\s*hour/i.exec(o.duration);
        return m ? { hours: parseInt(m[1], 10), opt: o } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.hours - a.hours);

    const bestFor = (h) => {
      const exact = fixed.find((t) => t.hours === h);
      if (exact) return { price: exact.opt.price, mode: "fixed", underlying: exact.opt };
      let remaining = h;
      let total = 0;
      for (const t of fixed) {
        while (remaining >= t.hours) { total += t.opt.price; remaining -= t.hours; }
      }
      total += remaining * perHourRate;
      total = Math.min(total, h * perHourRate);
      return { price: total, mode: "perhour", underlying: perHour, hours: h };
    };

    const maxHours = Math.max(8, ...fixed.map((t) => t.hours));
    return Array.from({ length: maxHours }, (_, i) => {
      const h = i + 1;
      const r = bestFor(h);
      return {
        duration: `${h} Hour${h > 1 ? "s" : ""}`,
        price: r.price,
        mode: r.mode,
        underlying: r.underlying,
        hours: r.hours || h,
      };
    });
  }, [selectedPackage]);

  const selectDisplayOption = (o) => {
    if (o.mode === "fixed") {
      setOptionDuration(o.underlying.duration);
      setHours(1);
    } else {
      setOptionDuration(o.underlying.duration); // "Per Hour"
      setHours(o.hours);
    }
  };

  const activeDisplayKey = useMemo(() => {
    if (!optionDuration) return null;
    if (isPerHour) return `${effectiveHours} Hour${effectiveHours > 1 ? "s" : ""}`;
    return optionDuration;
  }, [optionDuration, isPerHour, effectiveHours]);

  const unitPrice = selectedOption?.price || 0;
  const perHourTotal = isPerHour ? unitPrice * effectiveHours : 0;

  // Fixed-duration options in this package, largest-first (for combo pricing).
  const fixedTiers = useMemo(() => {
    if (!selectedPackage) return [];
    return selectedPackage.options
      .filter((o) => !o.duration.toLowerCase().startsWith("per hour"))
      .map((o) => {
        const m = o.duration.match(/\d+/);
        return { hours: m ? parseInt(m[0], 10) : 0, price: o.price, duration: o.duration };
      })
      .filter((t) => t.hours > 0)
      .sort((a, b) => b.hours - a.hours);
  }, [selectedPackage]);

  // Greedy combo breakdown: eg 6h → [4Hr pkg, 4Hr pkg? no] then 2 × per-hour
  const combo = useMemo(() => {
    if (!isPerHour || !effectiveHours) return null;
    let remaining = effectiveHours;
    const parts = [];
    let total = 0;
    for (const tier of fixedTiers) {
      while (remaining >= tier.hours) {
        parts.push({ label: `${tier.duration} package`, price: tier.price, hours: tier.hours });
        total += tier.price;
        remaining -= tier.hours;
      }
    }
    if (remaining > 0) {
      parts.push({ label: `${remaining} × Per Hour`, price: remaining * unitPrice, hours: remaining });
      total += remaining * unitPrice;
    }
    // Compare with straight N × per_hour, take cheaper
    if (perHourTotal < total) {
      return { parts: [{ label: `${effectiveHours} × Per Hour`, price: perHourTotal, hours: effectiveHours }], total: perHourTotal, hasDiscount: false };
    }
    return { parts, total, hasDiscount: total < perHourTotal };
  }, [isPerHour, effectiveHours, fixedTiers, unitPrice, perHourTotal]);

  const effectivePrice = isPerHour ? (combo?.total ?? unitPrice) : unitPrice;
  const savings = combo?.hasDiscount ? perHourTotal - combo.total : 0;

  // Discount + final payable
  const couponDiscount = couponApplied?.discount || 0;
  const finalPayable = Math.max(0, effectivePrice - couponDiscount);

  // Reset coupon whenever the underlying price changes (package/duration/hours)
  useEffect(() => {
    setCouponApplied(null);
    setCouponError("");
  }, [packageId, optionDuration, hours]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setPackageId(initialPackage?.id || "");
      setOptionDuration(initialOption?.duration || "");
      setHours(1);
      setDate(undefined);
      setStartTime("");
      setSuccess(false);
      setWaConfirmUrl("");
      setDetails({ name: "", email: "", phone: "", notes: "" });
      setCouponCode("");
      setCouponApplied(null);
      setCouponError("");
    }
  }, [open, initialPackage, initialOption]);

  useEffect(() => {
    const fetchAvail = async () => {
      if (!date) return;
      try {
        const d = format(date, "yyyy-MM-dd");
        const params = { date: d };
        if (optionDuration) {
          params.duration = isPerHour && effectiveHours > 1
            ? `${effectiveHours} Hours`
            : optionDuration;
        }
        const res = await axios.get(`${API}/bookings/availability`, { params });
        setBookedSlots(res.data.booked || []);
      } catch (err) {
        console.error("Availability fetch failed:", err);
        setBookedSlots([]);
      }
    };
    fetchAvail();

    // Poll while user is picking a slot so admin deletes / expired holds
    // become visible without a page refresh.
    if (step === 3 && date) {
      const iv = setInterval(fetchAvail, 20000);
      return () => clearInterval(iv);
    }
    return undefined;
  }, [date, optionDuration, isPerHour, effectiveHours, step]);

  const canNext = () => {
    if (step === 1) return !!packageId;
    if (step === 2) return !!optionDuration;
    if (step === 3) return !!date && !!startTime;
    if (step === 4) return details.name && details.email && details.phone.length >= 10;
    return true;
  };

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    setCouponLoading(true);
    setCouponError("");
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, {
        code,
        amount: effectivePrice,
      });
      setCouponApplied({
        code: data.code,
        discount: data.discount,
        finalAmount: data.final_amount,
        type: data.discount_type,
        value: data.discount_value,
      });
      toast.success(`Coupon ${data.code} applied · You save ₹${data.discount.toLocaleString("en-IN")}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid coupon";
      setCouponApplied(null);
      setCouponError(msg);
      toast.error(msg);
    } finally {
      setCouponLoading(false);
    }
  };

  const clearCoupon = () => {
    setCouponApplied(null);
    setCouponCode("");
    setCouponError("");
  };

  const openRazorpayCheckout = ({ orderId, keyId, amountPaise, bookingId }) => {
    return new Promise((resolve, reject) => {
      if (typeof window === "undefined" || !window.Razorpay) {
        reject(new Error("Razorpay SDK not loaded"));
        return;
      }

      // ---- Fix: Radix Dialog locks body pointer-events + traps focus, which
      // blocks clicks/typing inside the Razorpay iframe. Restore pointer-events
      // on body and stop the focus trap from re-stealing focus while Razorpay
      // is open. Cleanup runs on close/failure/success.
      const prevBodyPE = document.body.style.pointerEvents;

      const focusStealer = (e) => {
        const rzpEl = document.querySelector(".razorpay-container");
        if (rzpEl && !rzpEl.contains(e.target)) {
          e.stopImmediatePropagation();
        }
      };

      const applyRazorpayGuards = () => {
        document.body.style.pointerEvents = "auto";
        document.body.classList.add("razorpay-open");
        document.addEventListener("focusin", focusStealer, true);
      };
      const releaseRazorpayGuards = () => {
        document.body.style.pointerEvents = prevBodyPE;
        document.body.classList.remove("razorpay-open");
        document.removeEventListener("focusin", focusStealer, true);
      };

      const options = {
        key: keyId,
        amount: amountPaise,
        currency: "INR",
        order_id: orderId,
        name: "LIME Studios",
        description: `${selectedPackage?.name} · ${isPerHour ? `${effectiveHours} Hour${effectiveHours > 1 ? "s" : ""}` : selectedOption?.duration}`,
        image: "/favicon.svg",
        prefill: {
          name: details.name,
          email: details.email,
          contact: details.phone,
        },
        notes: {
          booking_id: bookingId,
          package: selectedPackage?.name,
          duration: selectedOption?.duration,
          date: date ? format(date, "yyyy-MM-dd") : "",
          start_time: startTime,
        },
        theme: { color: "#E4FF00" },
        modal: {
          ondismiss: () => {
            releaseRazorpayGuards();
            setLoading(false);
            toast.message("Payment cancelled. Your slot isn't reserved yet.");
            reject(new Error("USER_DISMISSED"));
          },
        },
        handler: (response) => {
          releaseRazorpayGuards();
          resolve(response);
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        releaseRazorpayGuards();
        const err = resp?.error;
        toast.error(err?.description || "Payment failed. Please try again.");
        setLoading(false);
        reject(new Error(err?.description || "payment.failed"));
      });

      applyRazorpayGuards();
      rzp.open();

      // Radix FocusScope runs on the next tick — keep re-asserting pointer-events
      // for ~1s to survive its focus-return cycle in every browser.
      let ticks = 0;
      const keepAliveInterval = setInterval(() => {
        if (ticks++ > 20 || !document.querySelector(".razorpay-container")) {
          clearInterval(keepAliveInterval);
          return;
        }
        document.body.style.pointerEvents = "auto";
      }, 50);
    });
  };

  const handleRazorpay = async () => {
    if (!selectedPackage || !selectedOption || !date || !startTime) return;
    if (typeof window === "undefined" || !window.Razorpay) {
      toast.error("Payment SDK is still loading — try again in a moment.");
      return;
    }

    setLoading(true);

    let bookingId = null;
    let orderIdForCleanup = null;
    try {
      // 1. Create order on backend (also creates the pending booking)
      const { data: order } = await axios.post(`${API}/bookings/create-order`, {
        package_id: selectedPackage.id,
        option_duration: selectedOption.duration,
        amount: finalPayable,
        hours: isPerHour ? effectiveHours : 1,
        name: details.name,
        email: details.email,
        phone: details.phone,
        booking_date: format(date, "yyyy-MM-dd"),
        start_time: startTime,
        notes: details.notes,
        coupon_code: couponApplied?.code || "",
      });
      bookingId = order.booking_id;
      orderIdForCleanup = order.razorpay_order_id;

      // 2. Open Razorpay checkout, wait for handler
      const payResp = await openRazorpayCheckout({
        orderId: order.razorpay_order_id,
        keyId: order.razorpay_key_id,
        amountPaise: order.amount,
        bookingId,
      });

      // 3. Verify signature on backend
      const { data: verify } = await axios.post(`${API}/bookings/verify`, {
        booking_id: bookingId,
        razorpay_order_id: payResp.razorpay_order_id,
        razorpay_payment_id: payResp.razorpay_payment_id,
        razorpay_signature: payResp.razorpay_signature,
      });
      // Signature verified — clear cleanup handle so we don't accidentally release a paid slot
      orderIdForCleanup = null;

      // 4. Build WhatsApp confirmation URL (shown as a button on the success step)
      const durLabel = isPerHour
        ? `${effectiveHours} Hour${effectiveHours > 1 ? "s" : ""}${savings > 0 ? ` (best combo · saved ₹${savings.toLocaleString("en-IN")})` : ` × ₹${unitPrice.toLocaleString("en-IN")}/hr`}`
        : selectedOption.duration;
      const lines = [
        "*Booking Confirmed — Payment Received*",
        "",
        `*Name:* ${details.name}`,
        `*Phone:* ${details.phone}`,
        `*Email:* ${details.email}`,
        "",
        `*Package:* ${selectedPackage.name}`,
        `*Duration:* ${durLabel}`,
        ...(couponApplied ? [
          `*Base Amount:* ₹${effectivePrice.toLocaleString("en-IN")}`,
          `*Coupon:* ${couponApplied.code} (−₹${couponApplied.discount.toLocaleString("en-IN")})`,
        ] : []),
        `*Amount Paid:* ₹${finalPayable.toLocaleString("en-IN")}`,
        `*Date:* ${format(date, "EEE, dd MMM yyyy")}`,
        `*Start Time:* ${startTime}`,
        "",
        `*Payment ID:* ${verify.payment_id}`,
        `*Booking ID:* ${verify.booking_id}`,
      ];
      if (details.notes) {
        lines.push("", `*Notes:* ${details.notes}`);
      }
      lines.push("", "Payment successful via Razorpay. Slot is now blocked.");
      const message = lines.join("\n");
      const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
      setWaConfirmUrl(waUrl);

      setSuccess(true);
      setStep(5);
      toast.success(`Payment successful · ${verify.payment_id}`);
    } catch (err) {
      // Release the held slot immediately on ANY failure after order was created
      if (bookingId && orderIdForCleanup) {
        try {
          await axios.post(`${API}/bookings/cancel-hold`, {
            booking_id: bookingId,
            razorpay_order_id: orderIdForCleanup,
          });
        } catch (_) { /* best-effort */ }
      }

      if (err?.message === "USER_DISMISSED") return; // already handled by modal.ondismiss
      const status = err?.response?.status;
      let msg = "Payment failed. Try again.";
      if (status === HTTP_CONFLICT) {
        msg = "This slot was just booked by someone else. Please pick another time.";
      } else if (status === 400 && err?.response?.data?.detail?.includes?.("signature")) {
        msg = "Payment could not be verified. If you were charged, contact us with your payment ID.";
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#0a0a0a] border-[#262626] text-[#f4f4f0] max-w-2xl rounded-none p-0 overflow-hidden flex flex-col max-h-[92dvh] sm:max-h-[90vh]"
      >
        <div className="border-b border-[#262626] px-6 py-5 flex flex-wrap items-center gap-4 justify-between shrink-0">
          <DialogHeader className="p-0 space-y-1">
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">
              Book your slot
            </DialogTitle>
            <DialogDescription className="text-[#A3A3A3] text-sm">
              5 quick steps. Pay online or chat on WhatsApp.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3">
            <StepBadge n={1} label="Package" active={step === 1} done={step > 1} />
            <StepBadge n={2} label="Duration" active={step === 2} done={step > 2} />
            <StepBadge n={3} label="Slot" active={step === 3} done={step > 3} />
            <StepBadge n={4} label="Details" active={step === 4} done={step > 4} />
            <StepBadge n={5} label="Done" active={step === 5} done={success} />
          </div>
        </div>

        <div className="px-6 py-6 flex-1 min-h-0 overflow-y-auto">
          {step === 1 && (
            <div className="space-y-4">
              <Label className="font-mono-overline text-[#E4FF00]">Choose a package</Label>
              <p className="text-xs text-[#A3A3A3]">Pick the service you need. You&apos;ll choose the duration next.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {packages.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`pick-package-${p.id}`}
                    onClick={() => { setPackageId(p.id); setOptionDuration(""); }}
                    className={`text-left p-4 border transition-colors ${
                      packageId === p.id
                        ? "border-[#E4FF00] bg-[#141414]"
                        : "border-[#262626] hover:border-[#404040] bg-[#0a0a0a]"
                    }`}
                  >
                    <div className="font-display font-bold text-base">{p.name}</div>
                    <div className="text-xs text-[#A3A3A3] mt-1 line-clamp-2">{p.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && selectedPackage && (
            <div className="space-y-4">
              <div>
                <Label className="font-mono-overline text-[#E4FF00]">Pick a duration</Label>
                <p className="text-xs text-[#A3A3A3] mt-1">For <span className="text-[#f4f4f0] font-medium">{selectedPackage.name}</span></p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {displayOptions.map((o) => (
                  <button
                    key={o.duration}
                    data-testid={`pick-duration-${o.duration}`}
                    onClick={() => selectDisplayOption(o)}
                    className={`p-3 border text-left transition-colors ${
                      activeDisplayKey === o.duration
                        ? "border-[#E4FF00] bg-[#141414]"
                        : "border-[#262626] hover:border-[#404040]"
                    }`}
                  >
                    <div className="text-[10px] text-[#A3A3A3] font-mono uppercase tracking-wider">{o.duration}</div>
                    <div className="text-lg font-display font-black mt-1 flex items-center">
                      <IndianRupee className="h-4 w-4 mr-0.5" />{o.price.toLocaleString("en-IN")}
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#666] font-mono">Rates auto-update with the latest pricing. Longer sessions use the best combo automatically.</p>

              {isPerHour && (
                <div className="border border-[#262626] p-4 bg-[#0f0f0f] space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-mono-overline text-[#E4FF00]">How many hours?</Label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setHours((h) => Math.max(1, Number(h) - 1))}
                        disabled={effectiveHours <= 1}
                        data-testid="hours-decrement"
                        className="h-9 w-9 border border-[#262626] text-[#f4f4f0] hover:border-[#E4FF00] hover:text-[#E4FF00] disabled:opacity-30 flex items-center justify-center text-lg"
                      >−</button>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={hours}
                        onChange={(e) => {
                          const v = Math.max(1, Math.min(12, parseInt(e.target.value || "1", 10)));
                          setHours(v);
                        }}
                        data-testid="hours-input"
                        className="w-16 h-9 text-center bg-[#0a0a0a] border border-[#262626] font-display font-bold text-lg focus:border-[#E4FF00] outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setHours((h) => Math.min(12, Number(h) + 1))}
                        disabled={effectiveHours >= 12}
                        data-testid="hours-increment"
                        className="h-9 w-9 border border-[#262626] text-[#f4f4f0] hover:border-[#E4FF00] hover:text-[#E4FF00] disabled:opacity-30 flex items-center justify-center text-lg"
                      >+</button>
                    </div>
                  </div>
                  <div className="border-t border-[#262626] pt-3 space-y-1.5">
                    {combo?.parts.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs font-mono text-[#A3A3A3]">
                        <span>{p.label}</span>
                        <span>₹{p.price.toLocaleString("en-IN")}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-2 border-t border-[#262626]">
                      <div className="text-xs font-mono uppercase tracking-widest text-[#A3A3A3]">Total ({effectiveHours} hr{effectiveHours > 1 ? "s" : ""})</div>
                      <div className="text-right">
                        {savings > 0 && (
                          <div className="text-xs font-mono line-through text-[#666]">
                            ₹{perHourTotal.toLocaleString("en-IN")}
                          </div>
                        )}
                        <div className="font-display text-2xl font-black flex items-center text-[#E4FF00]">
                          <IndianRupee className="h-5 w-5 mr-0.5" />{effectivePrice.toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>
                  </div>

                  {savings > 0 && (
                    <div
                      data-testid="discount-applied"
                      className="mt-2 p-3 border border-[#E4FF00]/60 bg-[#E4FF00]/[0.06] flex items-center gap-2 text-xs leading-relaxed"
                    >
                      <svg viewBox="0 0 20 20" className="h-4 w-4 shrink-0 fill-[#E4FF00]">
                        <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm-1 12l-4-4 1.4-1.4L9 11.2l5.6-5.6L16 7l-7 7z"/>
                      </svg>
                      <div className="text-[#f4f4f0]">
                        <span className="font-bold text-[#E4FF00]">Best combo rate applied.</span>{" "}
                        You saved ₹{savings.toLocaleString("en-IN")} vs {effectiveHours} × per-hour.
                      </div>
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs text-[#666] font-mono">
                * Prices exclude GST. 1-hour prep + 1-hour wrap-up buffer applies between bookings.
              </p>
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="font-mono-overline text-[#E4FF00]">Pick a date</Label>
                <div className="mt-3 border border-[#262626] bg-[#0a0a0a]">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    className="text-[#f4f4f0]"
                  />
                </div>
              </div>
              <div>
                <Label className="font-mono-overline text-[#E4FF00]">Start time</Label>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {TIME_SLOTS.map((t) => {
                    const isBooked = bookedSlots.includes(t);
                    return (
                      <button
                        key={t}
                        disabled={isBooked || !date}
                        data-testid={`pick-time-${t}`}
                        onClick={() => setStartTime(t)}
                        className={`py-2 text-sm font-mono border transition-colors ${timeSlotClass(startTime === t, isBooked)}`}
                      >
                        {formatTime12h(t)}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#A3A3A3] mt-3">Available 24×7. Grey slots are booked (includes a 1-hour prep/wrap buffer between bookings).</p>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono-overline text-[#E4FF00]">Full Name</Label>
                  <Input data-testid="booking-name-input" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]" placeholder="Your name" />
                </div>
                <div>
                  <Label className="font-mono-overline text-[#E4FF00]">Phone</Label>
                  <Input data-testid="booking-phone-input" value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]" placeholder="+91 9xxxxxxxxx" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-mono-overline text-[#E4FF00]">Email</Label>
                  <Input data-testid="booking-email-input" type="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]" placeholder="you@brand.com" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-mono-overline text-[#E4FF00]">Project notes (optional)</Label>
                  <Textarea data-testid="booking-notes-input" value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]" placeholder="Tell us about your shoot, guests, props…" rows={3} />
                </div>
              </div>

              <div className="border border-[#262626] p-4 bg-[#0f0f0f]">
                <div className="text-xs font-mono uppercase tracking-widest text-[#A3A3A3]">Booking summary</div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-display text-lg font-bold">{selectedPackage?.name}</div>
                    <div className="text-sm text-[#A3A3A3]">
                      {isPerHour
                        ? `${effectiveHours} Hour${effectiveHours > 1 ? "s" : ""}${savings > 0 ? " · best combo rate" : ""}`
                        : selectedOption?.duration}
                      {" · "}{date ? format(date, "dd MMM yyyy") : ""} · {startTime}
                    </div>
                  </div>
                  <div className="text-right">
                    {couponApplied ? (
                      <>
                        <div className="text-sm text-[#A3A3A3] line-through">₹{effectivePrice.toLocaleString("en-IN")}</div>
                        <div className="text-3xl font-display font-black flex items-center justify-end text-[#E4FF00]">
                          <IndianRupee className="h-6 w-6" />{finalPayable.toLocaleString("en-IN")}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#E4FF00] mt-1">
                          {couponApplied.code} · saved ₹{couponApplied.discount.toLocaleString("en-IN")}
                        </div>
                      </>
                    ) : (
                      <div className="text-3xl font-display font-black flex items-center"><IndianRupee className="h-6 w-6" />{effectivePrice.toLocaleString("en-IN")}</div>
                    )}
                  </div>
                </div>

                {/* Coupon input */}
                <div className="mt-4 pt-4 border-t border-[#262626]">
                  {couponApplied ? (
                    <div className="flex items-center justify-between gap-3" data-testid="booking-coupon-applied">
                      <div className="text-xs">
                        <span className="text-[#E4FF00] font-mono uppercase tracking-widest">Coupon applied</span>
                        <div className="text-[#A3A3A3] mt-1">
                          <span className="text-[#f4f4f0] font-bold">{couponApplied.code}</span>
                          {" · "}
                          {couponApplied.type === "percent"
                            ? `${couponApplied.value}% off`
                            : `₹${couponApplied.value} off`}
                          {" · "}
                          You save ₹{couponApplied.discount.toLocaleString("en-IN")}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={clearCoupon}
                        data-testid="booking-coupon-remove"
                        className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent text-xs h-auto py-1"
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Label className="font-mono-overline text-[#A3A3A3]">Have a coupon?</Label>
                      <div className="mt-2 flex gap-2">
                        <Input
                          data-testid="booking-coupon-input"
                          value={couponCode}
                          onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(""); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                          placeholder="ENTER CODE"
                          className="bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00] uppercase tracking-widest"
                        />
                        <Button
                          type="button"
                          onClick={applyCoupon}
                          disabled={!couponCode.trim() || couponLoading}
                          data-testid="booking-coupon-apply"
                          className="bg-transparent border border-[#E4FF00] text-[#E4FF00] hover:bg-[#E4FF00] hover:text-black rounded-none font-bold text-xs px-4"
                        >
                          {couponLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
                        </Button>
                      </div>
                      {couponError && (
                        <div className="mt-2 text-xs text-red-400 font-mono" data-testid="booking-coupon-error">{couponError}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <p className="text-xs text-[#A3A3A3] leading-relaxed">
                Clicking <span className="text-[#f4f4f0] font-medium">&quot;Pay&quot;</span> opens Razorpay to confirm your slot instantly. Your slot is only locked once payment goes through.
              </p>
            </div>
          )}

          {step === 5 && success && (
            <div className="text-center py-10 space-y-4">
              <div className="relative inline-block">
                <span className="absolute -inset-4 rounded-full bg-[#E4FF00]/20 blur-2xl" />
                <svg viewBox="0 0 24 24" className="relative h-20 w-20 fill-[#E4FF00] mx-auto">
                  <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm-1 15l-5-5 1.4-1.4L11 14.2l6.6-6.6L19 9l-8 8z"/>
                </svg>
              </div>
              <h3 className="font-display text-3xl font-black">Payment successful.</h3>
              <p className="text-[#A3A3A3] max-w-md mx-auto text-sm">
                Your slot is locked. A confirmation email is on its way. You can also share the booking summary on WhatsApp below.
              </p>
              <Badge className="bg-[#141414] border border-[#262626] text-[#A3A3A3] font-mono text-xs">
                {selectedPackage?.name} · {isPerHour ? `${effectiveHours} Hour${effectiveHours > 1 ? "s" : ""}` : selectedOption?.duration} · {date ? format(date, "dd MMM yyyy") : ""} · {startTime}
              </Badge>
              {waConfirmUrl && (
                <div className="pt-2">
                  <a
                    href={waConfirmUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="booking-share-whatsapp"
                    className="inline-flex items-center gap-2 bg-[#25D366] text-black hover:bg-[#20b859] rounded-none font-bold px-6 py-3 transition-colors"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                      <path d="M19.05 4.91A10.07 10.07 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.45 3.45 1.31 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.02z"/>
                    </svg>
                    Share confirmation on WhatsApp
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {step < 5 && (
          <div className="border-t border-[#262626] px-6 py-4 flex items-center justify-between shrink-0 bg-[#0a0a0a]">
            <Button
              variant="ghost"
              data-testid="booking-back-button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < 4 ? (
              <Button
                data-testid="booking-next-button"
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext()}
                className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold px-6"
              >
                Continue <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                data-testid="booking-razorpay-button"
                onClick={handleRazorpay}
                disabled={!canNext() || loading}
                className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold px-5"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <IndianRupee className="h-4 w-4 mr-1" />}
                Pay {finalPayable.toLocaleString("en-IN")}
              </Button>
            )}
          </div>
        )}

        {step === 5 && success && (
          <div className="border-t border-[#262626] px-6 py-4 flex justify-end shrink-0 bg-[#0a0a0a]">
            <Button
              data-testid="booking-close-button"
              onClick={() => onOpenChange(false)}
              className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold px-6"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
